import { API_BASE_URL } from "@kupikupi/brand";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  estimateInternational,
  getEstimateCountries,
  getLogisticsCategories,
  getServiceItems,
} from "../endpoints/logistics";
import { useBackendLang } from "../lang-context";
import type {
  EstimateCountryGroups,
  EstimateInput,
  LogisticsCategory,
  LogisticsRoute,
  ServiceItem,
} from "../types/logistics";

type Currency = "RUB" | "CNY" | "USD";

/**
 * Destination-country options for the freight calculator. Public — loads
 * signed-out. Grouped (HOT / A-Z / China) exactly as the backend buckets them.
 */
export function useEstimateCountries(currency?: Currency) {
  const lang = useBackendLang();
  return useQuery<EstimateCountryGroups>({
    queryKey: ["logistics", "countries", lang, currency],
    queryFn: () =>
      getEstimateCountries(
        { baseUrl: API_BASE_URL },
        currency === undefined ? { lang } : { lang, currency },
      ),
    staleTime: 60 * 60 * 1000,
  });
}

/** Shippable product-category tree for the estimate form's category filter. */
export function useLogisticsCategories(currency?: Currency) {
  const lang = useBackendLang();
  return useQuery<LogisticsCategory[]>({
    queryKey: ["logistics", "categories", lang, currency],
    queryFn: () =>
      getLogisticsCategories(
        { baseUrl: API_BASE_URL },
        currency === undefined ? { lang } : { lang, currency },
      ),
    staleTime: 60 * 60 * 1000,
  });
}

/** Enabled warehouse packing/value services (rehearsal page toggles). */
export function useServiceItems(currency?: Currency) {
  const lang = useBackendLang();
  return useQuery<ServiceItem[]>({
    queryKey: ["logistics", "service-items", lang, currency],
    queryFn: () =>
      getServiceItems(
        { baseUrl: API_BASE_URL },
        currency === undefined ? { lang } : { lang, currency },
      ),
    staleTime: 30 * 60 * 1000,
  });
}

/**
 * International freight estimate — a mutation, since it's triggered by the
 * "calculate" button with the current form values (not an ambient query).
 * Routes come back already converted to the requested display currency.
 */
export function useEstimateInternational(currency?: Currency) {
  const lang = useBackendLang();
  return useMutation<LogisticsRoute[], Error, EstimateInput>({
    mutationFn: (input) =>
      estimateInternational(
        { baseUrl: API_BASE_URL },
        input,
        currency === undefined ? { lang } : { lang, currency },
      ),
  });
}
